import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Home } from "lucide-react";
import { api } from "@/lib/api";

const MAX_POLLS = 8;

export default function DonationSuccess() {
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id") || params.get("token");
    if (!sessionId) { setState("error"); return; }

    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        setAmount(data.amount);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch {
        setState("error");
        return;
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A1128] text-white grain flex items-center justify-center px-5">
      <div className="relative z-10 max-w-md w-full text-center bg-white text-[#0A1128] p-10 border-t-4 border-[#D90429]">
        {state === "checking" && (
          <div data-testid="donation-checking">
            <Loader2 className="w-14 h-14 text-[#D90429] mx-auto animate-spin" />
            <h1 className="font-display text-3xl uppercase mt-6">Confirming…</h1>
            <p className="text-slate-500 mt-2">Verifying your contribution.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="donation-paid">
            <CheckCircle2 className="w-16 h-16 text-[#D90429] mx-auto" />
            <h1 className="font-display text-4xl uppercase mt-6">Thank You, Patriot</h1>
            <p className="text-slate-600 mt-3">
              Your donation{amount ? ` of $${amount}` : ""} strengthens the mission.
              Together we stand prepared.
            </p>
          </div>
        )}
        {state === "pending" && (
          <div data-testid="donation-pending">
            <Loader2 className="w-14 h-14 text-[#F4A261] mx-auto" />
            <h1 className="font-display text-3xl uppercase mt-6">Processing</h1>
            <p className="text-slate-500 mt-2">
              Your payment is still processing. You'll receive a confirmation shortly.
            </p>
          </div>
        )}
        {state === "error" && (
          <div data-testid="donation-error">
            <XCircle className="w-16 h-16 text-slate-400 mx-auto" />
            <h1 className="font-display text-3xl uppercase mt-6">Something Went Wrong</h1>
            <p className="text-slate-500 mt-2">We couldn't confirm this donation.</p>
          </div>
        )}
        <Link
          to="/"
          data-testid="back-home-button"
          className="inline-flex items-center gap-2 mt-8 bg-[#0A1128] text-white px-8 py-3 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#1a2547] transition-colors duration-200"
        >
          <Home className="w-4 h-4" /> Back Home
        </Link>
      </div>
    </div>
  );
}
